import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const isNativePlatform = Capacitor.isNativePlatform();

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
export const AUTH_STORAGE_KEY = 'swapwish.auth';

// Indicatore SINCRONO di sessione persistita: il ripristino della sessione
// Supabase è async (getSession), quindi all'avvio `userStore.user` è ancora
// null. Per evitare il flash del form di login a un utente già autenticato si
// controlla direttamente il token in localStorage (scritto write-through dallo
// storage adapter). Ottimistico: la validità reale è verificata da /loading.
export function hasPersistedAuthSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const session = parsed?.currentSession ?? parsed;
    return !!(session && (session.access_token || session.refresh_token));
  } catch {
    return false;
  }
}

if (!url || !anonKey) {
  console.error(
    'SwapWish: VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY non sono impostate. ' +
      'Copia .env.example in .env e compila i valori.'
  );
}

// createBrowserClient from @supabase/ssr silently overrides any custom storage
// with its own cookie-based adapter, breaking hydrateSession() (which reads localStorage)
// and making sessions unavailable for PostgREST JWT attachment.
// Using createClient from @supabase/supabase-js respects the storage option.
const localStorageAdapter = {
  getItem: (key: string): string | null => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      console.warn('[supabase storage getItem]', error);
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      console.warn('[supabase storage setItem]', error);
    }
  },
  removeItem: (key: string): void => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      console.warn('[supabase storage removeItem]', error);
    }
  },
};

// Su mobile la sessione vive ANCHE nello storage nativo (SharedPreferences /
// UserDefaults via @capacitor/preferences), che sopravvive fino alla
// DISINSTALLAZIONE dell'app: se la WebView perde il proprio localStorage
// (eviction, aggiornamenti di sistema), la copia nativa la ripristina e
// l'utente resta autenticato. Scrittura write-through su entrambi; lettura
// dal localStorage (sincrono, niente attese) con fallback nativo.
const hybridStorageAdapter = {
  async getItem(key: string): Promise<string | null> {
    const local = localStorageAdapter.getItem(key);
    if (local != null) return local;
    if (!isNativePlatform) return null;
    try {
      const { value } = await Preferences.get({ key });
      // Re-idrata la cache web così le letture sincrone successive funzionano.
      if (value != null) localStorageAdapter.setItem(key, value);
      return value;
    } catch (error) {
      console.warn('[supabase native storage getItem]', error);
      return null;
    }
  },
  async setItem(key: string, value: string): Promise<void> {
    localStorageAdapter.setItem(key, value);
    if (!isNativePlatform) return;
    try {
      await Preferences.set({ key, value });
    } catch (error) {
      console.warn('[supabase native storage setItem]', error);
    }
  },
  async removeItem(key: string): Promise<void> {
    localStorageAdapter.removeItem(key);
    if (!isNativePlatform) return;
    try {
      await Preferences.remove({ key });
    } catch (error) {
      console.warn('[supabase native storage removeItem]', error);
    }
  },
};

export const supabase: SupabaseClient = createClient(
  url ?? 'http://localhost:54321',
  anonKey ?? 'public-anon-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: AUTH_STORAGE_KEY,
      storage: hybridStorageAdapter,
    },
    realtime: {
      params: { eventsPerSecond: 10 },
    },
    global: {
      headers: { 'x-client-info': 'swapwish-mobile' },
    },
  }
);
